import type * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    clipboard,
    Link,
    PrettyURL,
    RevealableToolbar,
    RevealableToolbarItem,
    RevealableToolbarRoot,
} from '@gitbook/spine-react';

/**
 * Component to display the URL with a copy button.
 * Allow to add other controls as children.
 */
export function CopyableURL(props: {
    url: string;
    className?: string;
    children?: React.ReactNode;
    /**
     * When to show the copy button
     * @default "hover"
     */
    when?: 'always' | 'hover';
    /**
     * If true, the URL will be wrapped in a Link component.
     * @default true
     */
    link?: boolean;
}) {
    const { url, children, className, link = true, when = 'hover' } = props;

    const urlElement = (
        <span className="block truncate">
            <PrettyURL>{url}</PrettyURL>
        </span>
    );

    return (
        <RevealableToolbarRoot className={className}>
            {link ? (
                <Link href={url} target="_blank" onClick={(event) => event.stopPropagation()}>
                    {urlElement}
                </Link>
            ) : (
                urlElement
            )}
            <RevealableToolbar aria-label="URL Actions" when={when}>
                {when === 'always' ? (
                    <CopyButton url={url} />
                ) : (
                    <RevealableToolbarItem>
                        <CopyButton url={url} />
                    </RevealableToolbarItem>
                )}
                {children}
            </RevealableToolbar>
        </RevealableToolbarRoot>
    );
}

function CopyButton(props: { url: string }): React.ReactElement {
    return (
        <Button
            kind="blank"
            size="small"
            label="Copy URL"
            leadingIcon={Icon.Copy}
            className="m-nudge-icon text-muted hover:text-base"
            onPress={() => {
                clipboard.writeText(props.url);
            }}
            onClick={(event) => {
                event.preventDefault();
            }}
            iconOnly
        />
    );
}
